/**
 * Readout — label-above-value microblock used throughout the C2 chrome.
 *
 *   <Readout label="ALT" value={altFt} unit="FT" />
 *   <Readout label="LAT" value="40.123°" />
 *
 * Uppercase tertiary-text label, mono value with tabular figures so
 * adjacent readouts in a row align numerically. Optional accent on the
 * value when it represents a "live" or "primary" reading.
 */
import clsx from 'clsx';

interface Props {
  label: string;
  value: React.ReactNode;
  unit?: string;
  /** When true, value renders in amber instead of default text color. */
  accent?: boolean;
  /** When true, label + value sit on the same row. Default stacked. */
  inline?: boolean;
  className?: string;
}

export default function Readout({
  label,
  value,
  unit,
  accent = false,
  inline = false,
  className,
}: Props) {
  const labelClass = 'text-[9px] font-semibold uppercase tracking-[0.15em] text-iron-400';
  const valueClass = clsx(
    'font-mono text-sm tabular-nums leading-none',
    accent ? 'text-accent' : 'text-iron-100',
  );
  const unitClass = 'text-[10px] uppercase text-iron-400 ml-1';

  if (inline) {
    return (
      <div className={clsx('inline-flex items-baseline gap-1.5', className)}>
        <span className={labelClass}>{label}</span>
        <span className={valueClass}>{value}</span>
        {unit && <span className={unitClass}>{unit}</span>}
      </div>
    );
  }

  return (
    <div className={clsx('flex flex-col gap-0.5', className)}>
      <span className={labelClass}>{label}</span>
      <span className="flex items-baseline">
        <span className={valueClass}>{value}</span>
        {unit && <span className={unitClass}>{unit}</span>}
      </span>
    </div>
  );
}
